/**
 * Ask the browser to KEEP this origin's data.
 *
 * IndexedDB is "best-effort" storage by default: under disk pressure a browser may evict a whole
 * origin — every project, every GLB blob, every clip in the library — without asking and without
 * telling anyone afterwards. `navigator.storage.persist()` moves the origin into the "persistent"
 * bucket, which browsers evict only on an explicit user action. Until this module existed the app
 * called `navigator.storage.estimate()` (`importExport/preflight.ts`) to warn about free space but
 * never asked for the protection itself — `persist()` and `persisted()` were called zero times
 * across `src/` (audit §2.3 #3). In a product whose promise is "your data never leaves your
 * machine", that left the promise one disk-full away from "your data is gone".
 *
 * WHY THIS IS A MODULE AND NOT A LINE IN THE SAVE PATH. The request has to be TOTAL: a browser
 * without the API (older Safari, the node test environment), a browser that says no, and a
 * browser that throws out of `persisted()` all have to end as an ordinary return value. This is
 * called from the save path, right after the write has already landed on disk — a save that
 * succeeded must never be reported to the user as failed because asking for a favour went wrong.
 *
 * WHY IT DOES NOT REMEMBER THAT IT WAS CALLED. "Once per session" is real and it matters — a
 * refused request repeated on every save is a permission prompt on every save — but the session
 * is the *caller's* notion, not this module's: `createProjectController` is built once per editor
 * session and holds the flag (`requestPersistentStorageOnce`). Keeping the state there rather
 * than in module scope means a test that simulates a browser refresh by building a fresh
 * controller set genuinely re-runs this path, instead of being silently short-circuited by state
 * that outlived the thing it was scoped to.
 *
 * WHEN to ask is likewise the caller's decision: the first project save that actually reached
 * disk. Chrome answers from a heuristic and Firefox may show a doorhanger — either way the user
 * is then being asked about work they have just done, not about an abstraction on a blank
 * startup screen.
 */

/**
 * - `granted` — the origin is persistent, either because it already was or because the request
 *   succeeded. Nothing to tell the user.
 * - `denied` — the browser implements the API and said no. The data is still evictable and the
 *   user is the only one who can act on that, so this is the outcome worth surfacing.
 * - `unsupported` — no usable API, or the call failed. Nothing is known, so nothing is claimed:
 *   warning about eviction here would be inventing a fact.
 */
export type StoragePersistOutcome = 'granted' | 'denied' | 'unsupported';

/**
 * Request persistent storage for this origin, resolving to what the browser decided. Never
 * rejects, and never throws synchronously.
 */
export async function ensurePersistentStorage(): Promise<StoragePersistOutcome> {
  // Same shape of guard as `readStorageEstimate` in `importExport/preflight.ts`: the API is
  // typed as always-present by lib.dom and is absent in practice (node, older Safari).
  const storage: StorageManager | undefined =
    typeof navigator !== 'undefined' ? navigator.storage : undefined;
  if (!storage || typeof storage.persisted !== 'function' || typeof storage.persist !== 'function') {
    return 'unsupported';
  }

  try {
    // `persisted()` FIRST, always. `persist()` is the call that can raise a permission prompt,
    // and re-asking for something the browser has already granted is a prompt the user has
    // already answered once.
    if (await storage.persisted()) return 'granted';
    return (await storage.persist()) ? 'granted' : 'denied';
  } catch (error) {
    // Deliberately not an empty catch: the developer gets the reason, the caller gets a value.
    // Whatever went wrong here, the write that triggered it is already on disk.
    console.warn('[intch-motion-kit] could not request persistent storage', error);
    return 'unsupported';
  }
}
